import { ROUTES } from '@/configs/constants';
import { GroupMenu, Route } from '@/types/routes';
import { ROLE } from '@/types/enums';
import { getStorage } from '@/utils/browsers';
import { Settings, Tickets, UsersRound, Image, MapPin, BarChart3, Map, BookText, Activity, Building2, Megaphone, Layers, Boxes, Sparkles, Ticket, CircleHelp } from 'lucide-react';

// 2026-05-16 (Issue 2 fix): role 別 sidebar filter。
// BUSINESS user は自社 landmark / 自社 coupon-template のみ操作可、その他の menu (= /users, /sponsors,
// /setting, /omikuji-texts, /tier-presets, /motif-template-sets, /template-motifs, /library,
// /items, /operations-map, /business-dashboard, /event-tracking-dashboard) は API 認可で 403 に
// なるため sidebar から hide する (= 403 で AuthStore.clearAuthentication が発火するのを避ける)。
//
// BUSINESS が触れる sidebar:
//   - /landmarks (= 自社 landmark)
//   - /coupon-templates (= 自社 template)
// 「ユーザー管理」 (= /users) は ADMIN のみ。「事業者を新規追加」 も /users 配下なので隠蔽される。
const BUSINESS_ALLOWED_ROUTES = new Set<string>([
    ROUTES.landmarkManagement.href,
    ROUTES.couponTemplateManagement.href,
    // 2026-07-07: ヘルプは全 role が参照可 (静的ページ、API 不要)。
    ROUTES.help.href
]);

// 2026-07-07 (pitfall batch): LandmarkFormSheet 等の role gate 用に export 化。
// ADMIN=0 / END_USER=1 / BUSINESS=2。
export function getCurrentRoleId(): number | null {
    // AuthStore.info は localStorage 'info' に保存される (= login 時に setItem)。
    // role_id は number (= 0/1/2)、未ログイン時は null。
    const info = getStorage('info', true);
    if (info && typeof info === 'object' && typeof info.role_id === 'number') {
        return info.role_id;
    }
    return null;
}

// #region Title Page
export const getTitlePage = (pathname: string) => {
    if (!pathname) {
        return {
            title: ''
        };
    }
    let pathnameTmp = pathname;
    const findRoute = Object.values(ROUTES).find((value) => {
        if (value?.pathKey) {
            return pathnameTmp.includes(value.pathKey);
        }
        return pathnameTmp === value.href;
    });

    return findRoute ?? { title: '' };
};
// #rendregion

// #region Breadcrumb
const STATIC_ROUTES_BREADCRUMB: {
    [key: string]: Route[]
} = {
    [ROUTES.dashboard.href]: [ROUTES.dashboard],
    [ROUTES.button.href]: [ROUTES.button],
    [ROUTES.calendar.href]: [ROUTES.calendar],
    [ROUTES.pagination.href]: [ROUTES.pagination],
    [ROUTES.table.href]: [ROUTES.table]
};

export const getBreadcrumb = (pathname: string): Route[] => {
    if (!pathname) return [];
    switch (pathname) {
        case STATIC_ROUTES_BREADCRUMB.hasOwnProperty(pathname) && pathname:
            return STATIC_ROUTES_BREADCRUMB[pathname];

        default:
            return [];
    }
};
// #endregion

// #region Menu
export const getMenuList = (pathname: string): GroupMenu[] => {
    const roleId = getCurrentRoleId();
    const isBusiness = roleId === ROLE.BUSINESS;
    const allMenus = [
        {
            href: ROUTES.userManagement.href,
            label: ROUTES.userManagement.menuLabel,
            active: pathname === ROUTES.userManagement.href,
            icon: UsersRound,
            submenus: []
        },
        {
            href: ROUTES.itemManagement.href,
            label: ROUTES.itemManagement.menuLabel,
            active: pathname.includes(ROUTES.itemManagement.href),
            icon: Tickets,
            submenus: []
        },
        {
            href: ROUTES.libraryManagement.href,
            label: ROUTES.libraryManagement.menuLabel,
            active: pathname === ROUTES.libraryManagement.href,
            icon: Image,
            submenus: []
        },
        {
            href: ROUTES.landmarkManagement.href,
            label: ROUTES.landmarkManagement.menuLabel,
            active: pathname === ROUTES.landmarkManagement.href,
            icon: MapPin,
            submenus: []
        },
        {
            href: ROUTES.businessDashboard.href,
            label: ROUTES.businessDashboard.menuLabel,
            active: pathname === ROUTES.businessDashboard.href,
            icon: BarChart3,
            submenus: []
        },
        {
            href: ROUTES.operationsMap.href,
            label: ROUTES.operationsMap.menuLabel,
            active: pathname === ROUTES.operationsMap.href,
            icon: Map,
            submenus: []
        },
        {
            href: ROUTES.omikujiTextsManagement.href,
            label: ROUTES.omikujiTextsManagement.menuLabel,
            active: pathname === ROUTES.omikujiTextsManagement.href,
            icon: BookText,
            submenus: []
        },
        {
            href: ROUTES.eventTrackingDashboard.href,
            label: ROUTES.eventTrackingDashboard.menuLabel,
            active: pathname === ROUTES.eventTrackingDashboard.href,
            icon: Activity,
            submenus: []
        },
        // T3-B sponsored motif (PR 4, 2026-05-07)
        {
            href: ROUTES.sponsorManagement.href,
            label: ROUTES.sponsorManagement.menuLabel,
            active: pathname === ROUTES.sponsorManagement.href,
            icon: Building2,
            submenus: []
        },
        {
            href: ROUTES.sponsoredCampaignManagement.href,
            label: ROUTES.sponsoredCampaignManagement.menuLabel,
            active: pathname === ROUTES.sponsoredCampaignManagement.href,
            icon: Megaphone,
            submenus: []
        },
        // T3-B PR 4-bis (2026-05-12): omikuji ガチャフレームワーク管理 (= roadmap v3 §8)
        {
            href: ROUTES.tierPresetManagement.href,
            label: ROUTES.tierPresetManagement.menuLabel,
            active: pathname === ROUTES.tierPresetManagement.href,
            icon: Layers,
            submenus: []
        },
        {
            href: ROUTES.motifTemplateSetManagement.href,
            label: ROUTES.motifTemplateSetManagement.menuLabel,
            active: pathname === ROUTES.motifTemplateSetManagement.href,
            icon: Boxes,
            submenus: []
        },
        {
            href: ROUTES.templateMotifManagement.href,
            label: ROUTES.templateMotifManagement.menuLabel,
            active: pathname.startsWith(ROUTES.templateMotifManagement.href),
            icon: Sparkles,
            submenus: []
        },
        // Coupon management redesign (Phase 2 PR 4, 2026-05-15)
        {
            href: ROUTES.couponTemplateManagement.href,
            label: ROUTES.couponTemplateManagement.menuLabel,
            active: pathname === ROUTES.couponTemplateManagement.href,
            icon: Ticket,
            submenus: []
        },
        {
            href: ROUTES.setting.href,
            label: ROUTES.setting.menuLabel,
            active: pathname === ROUTES.setting.href,
            icon: Settings,
            submenus: []
        },
        // 2026-07-07: 操作・項目説明ヘルプ (全 role に表示)
        {
            href: ROUTES.help.href,
            label: ROUTES.help.menuLabel,
            active: pathname === ROUTES.help.href,
            icon: CircleHelp,
            submenus: []
        }
        // {
        //     href: ROUTES.button.href,
        //     label: ROUTES.button.menuLabel,
        //     active: pathname === ROUTES.button.href,
        //     icon: Settings,
        //     submenus: []
        // },
        // {
        //     href: ROUTES.calendar.href,
        //     label: ROUTES.calendar.menuLabel,
        //     active: pathname === ROUTES.calendar.href,
        //     icon: CalendarIcon,
        //     submenus: []
        // },
        // {
        //     href: ROUTES.pagination.href,
        //     label: ROUTES.pagination.menuLabel,
        //     active: pathname === ROUTES.pagination.href,
        //     icon: Columns3Icon,
        //     submenus: []
        // },
        // {
        //     href: ROUTES.table.href,
        //     label: ROUTES.table.menuLabel,
        //     active: pathname === ROUTES.table.href,
        //     icon: TableIcon,
        //     submenus: []
        // },
        // {
        //     href: ROUTES.reactToastify.href,
        //     label: ROUTES.reactToastify.menuLabel,
        //     active: pathname === ROUTES.reactToastify.href,
        //     icon: BellIcon,
        //     submenus: []
        // },
        // {
        //     href: ROUTES.modal.href,
        //     label: ROUTES.modal.menuLabel,
        //     active: pathname === ROUTES.modal.href,
        //     icon: CaptionsIcon,
        //     submenus: []
        // },
        // {
        //     href: ROUTES.tabs.href,
        //     label: ROUTES.tabs.menuLabel,
        //     active: pathname === ROUTES.tabs.href,
        //     icon: AppWindowIcon,
        //     submenus: []
        // }
    ];
    // 2026-05-16 (Issue 2 fix): BUSINESS は許可 menu のみ filter (= API 403 → logout を回避)
    const menus = isBusiness ?
        allMenus.filter(m => BUSINESS_ALLOWED_ROUTES.has(m.href)) :
        allMenus;
    return [
        {
            groupLabel: '',
            menus
        }
    ];
};
// #endregion
